import React, { useEffect, useRef, useState } from 'react';
import { Check, ImageIcon, RotateCcw } from 'lucide-react';
import {
  TaskyAlert,
  TaskyBadge,
  TaskyButton,
  TaskyCard,
  TaskyInput,
  TaskyProgress,
  TaskySegmentedControl,
  TaskySpinner,
} from '../tasky';
import { cn } from '../../lib/utils';
import easyVideoService, {
  type EasyVideoConflict,
  type EasyVideoCreditShortfall,
  type EasyVideoProject,
  type EasyVideoRetakeKind,
  type EasyVideoScene,
  type EasyVideoStage,
} from '../../services/api/easyVideoService';

export const EASY_VIDEO_POLL_INTERVAL_MS = 5000;

export interface EasyVideoProgressProps {
  project: EasyVideoProject;
  /** ポーリング・作り直しで新しい状態を受け取ったとき */
  onUpdate: (project: EasyVideoProject) => void;
  /** 見られなくなった（4xx）ときの戻り先（新規作成・作りかけ一覧） */
  onBack: () => void;
}

const STAGES: { key: EasyVideoStage; label: string }[] = [
  { key: 'script', label: '構成' },
  { key: 'bgm', label: 'BGM' },
  { key: 'storyboards', label: '絵コンテ' },
  { key: 'clips', label: '動画' },
  { key: 'compose', label: '仕上げ' },
];

export const RETAKE_CHIPS = ['商品を大きく', '商品の形を正確に', '人物なし', '明るく'];

const isTerminal = (status: EasyVideoProject['status']) => status === 'completed' || status === 'failed';

/** 残高不足（402）の案内。リーダーには axiosConfig 経由で購入モーダルも開く */
export const creditShortfallMessage = (lack: EasyVideoCreditShortfall): string =>
  (lack.requiredCredits !== null && lack.availableCredits !== null
    ? `クレジットが足りません（必要 約${lack.requiredCredits.toLocaleString()} cr／残り ${lack.availableCredits.toLocaleString()} cr）。`
    : 'クレジットが足りません。') + 'クレジットを追加してから、もう一度お試しください（購入は組織のリーダーが行えます）。';

export const conflictMessage = (conflict: EasyVideoConflict): string =>
  conflict === 'retake_limit'
    ? 'このシーンは作り直せる回数の上限に達しました。'
    : '最初にお伝えした上限のクレジットを超えるため、これ以上は作り直せません。';

/** 全体の進み具合（0〜100）。絵コンテ・動画の段はできたシーンの割合で刻む */
const overallPercent = (project: EasyVideoProject): number => {
  if (project.status === 'completed') return 100;
  const index = project.stage ? STAGES.findIndex((s) => s.key === project.stage) : 0;
  const scenes = project.scenes;
  let fraction = 0;
  if (scenes.length > 0 && (project.stage === 'storyboards' || project.stage === 'clips')) {
    const key = project.stage === 'storyboards' ? 'storyboard' : 'clip';
    fraction = scenes.filter((s) => s[key].status === 'done').length / scenes.length;
  }
  return Math.round(((Math.max(index, 0) + fraction) / STAGES.length) * 100);
};

const elapsedSeconds = (startedAt: string | null, now: number): number | null => {
  if (!startedAt) return null;
  const started = new Date(startedAt).getTime();
  if (Number.isNaN(started)) return null;
  return Math.max(0, Math.floor((now - started) / 1000));
};

/**
 * GET 詳細を 5 秒ごとに取り直す。前の応答が返ってから次を予約する（詳細取得は
 * 進行中ジョブの突き合わせで遅くなりうるため、リクエストを重ねない）。
 * completed / failed、4xx（削除・フラグOFF など）、アンマウントで止まる。5xx・通信失敗は次の周期で取り直す。
 */
const useEasyVideoPolling = (
  projectId: string,
  active: boolean,
  onUpdate: (project: EasyVideoProject) => void,
  onStop: (message: string) => void
) => {
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;
  const onStopRef = useRef(onStop);
  onStopRef.current = onStop;

  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const schedule = () => {
      timer = setTimeout(async () => {
        try {
          const next = await easyVideoService.get(projectId);
          if (cancelled) return;
          onUpdateRef.current(next);
          if (isTerminal(next.status)) return;
        } catch (e) {
          if (cancelled) return;
          const stopMessage = easyVideoService.extractPollingStopMessage(e);
          if (stopMessage) {
            onStopRef.current(stopMessage);
            return;
          }
          // 一時的な失敗は次の周期で取り直す
        }
        schedule();
      }, EASY_VIDEO_POLL_INTERVAL_MS);
    };

    schedule();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [projectId, active]);
};

/** 生成中カードの経過秒数用に 1 秒ごとに再描画する */
const useNow = (active: boolean): number => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
};

interface SceneCardProps {
  project: EasyVideoProject;
  scene: EasyVideoScene;
  now: number;
  onUpdate: (project: EasyVideoProject) => void;
}

const SceneCard: React.FC<SceneCardProps> = ({ project, scene, now, onUpdate }) => {
  const [open, setOpen] = useState(false);
  // 作り直せる対象。進行中の案件では、まだ投入していない（pending）・生成中の対象は選べない
  // （押すと自動進行の投入と競合する）。できた（done）か失敗した（failed）ものだけ
  const projectRunning = project.status === 'running';
  const settled = (status: EasyVideoScene['storyboard']['status']) => status === 'done' || status === 'failed';
  const retakableKinds: EasyVideoRetakeKind[] = [];
  if (!projectRunning || settled(scene.storyboard.status)) retakableKinds.push('storyboard');
  if (scene.storyboard.status === 'done' && (!projectRunning || settled(scene.clip.status))) retakableKinds.push('clip');
  const [kind, setKind] = useState<EasyVideoRetakeKind>(
    scene.storyboard.status === 'failed' || scene.clip.status === 'pending' ? 'storyboard' : 'clip'
  );
  const [chips, setChips] = useState<string[]>([]);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const running = scene.storyboard.status === 'running' || scene.clip.status === 'running';
  const failed = scene.storyboard.status === 'failed' || scene.clip.status === 'failed';
  const runningAsset = scene.clip.status === 'running' ? scene.clip : scene.storyboard;
  const elapsed = running ? elapsedSeconds(runningAsset.started_at, now) : null;
  const canChooseKind = retakableKinds.length === 2;
  const effectiveKind: EasyVideoRetakeKind = canChooseKind ? kind : retakableKinds[0] ?? 'storyboard';
  const left = scene.retakes_left[effectiveKind] ?? 0;
  // retake_unit.storyboard はサーバー側で「絵コンテ＋それに伴う動画の作り直し」込みの額
  const unit = project.credits.retake_unit[effectiveKind];
  const retakeDisabled = project.status === 'draft' || running || left <= 0 || submitting;

  const toggleChip = (chip: string) =>
    setChips((current) => (current.includes(chip) ? current.filter((c) => c !== chip) : [...current, chip]));

  const handleRetake = async () => {
    setSubmitting(true);
    setMessage(null);
    const hint = [...chips, note.trim()].filter(Boolean).join('、');
    try {
      const next = await easyVideoService.retake(project.id, scene.id, effectiveKind, hint || undefined);
      onUpdate(next);
      setOpen(false);
      setChips([]);
      setNote('');
    } catch (e) {
      const conflict = easyVideoService.extractConflict(e);
      if (conflict) setMessage(conflictMessage(conflict));
      else {
        const lack = easyVideoService.extractCreditShortfall(e);
        if (lack) setMessage(creditShortfallMessage(lack));
        else setMessage(easyVideoService.extractErrorMessage(e));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const aspectClass = project.aspect_ratio === '16:9' ? 'aspect-video' : 'aspect-[9/16]';

  return (
    <TaskyCard variant="bordered" padding="sm" className="flex flex-col gap-3" data-testid={`easy-video-scene-${scene.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-dark">シーン {scene.order + 1}</span>
        <span className="text-xs text-gray-500">{scene.duration_sec}秒</span>
      </div>

      <div className={cn('relative w-full overflow-hidden rounded-tasky-sm bg-gray-100', aspectClass)}>
        {scene.clip.status === 'done' && scene.clip.url ? (
          <video
            src={scene.clip.url}
            controls
            muted
            playsInline
            loop
            className="h-full w-full object-cover"
            data-testid="easy-video-scene-clip"
          />
        ) : scene.storyboard.url ? (
          <img src={scene.storyboard.url} alt={`シーン${scene.order + 1}の絵コンテ`} className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-gray-400">
            <ImageIcon className="w-8 h-8" />
          </div>
        )}
        {running && (
          <div className="absolute inset-x-0 bottom-0 flex items-center gap-2 bg-white/90 px-2 py-1 text-xs text-dark">
            <TaskySpinner size="sm" />
            {scene.clip.status === 'running' ? '動画を作成中' : '絵コンテを作成中'}
            {elapsed !== null && `… ${elapsed}秒`}
          </div>
        )}
      </div>

      <p className="text-sm text-gray-700">{scene.summary}</p>

      {failed && (
        <TaskyAlert tone="error">{scene.error_message || 'このシーンの作成に失敗しました。作り直してください。'}</TaskyAlert>
      )}

      {retakableKinds.length === 0 ? null : !open ? (
        <TaskyButton
          variant={failed ? 'default' : 'secondary'}
          size="sm"
          disabled={retakeDisabled}
          onClick={() => setOpen(true)}
        >
          <RotateCcw />
          作り直す（+{unit}cr・残り{left}回）
        </TaskyButton>
      ) : (
        <div className="space-y-2" data-testid="easy-video-retake-panel">
          {canChooseKind && (
            <TaskySegmentedControl<EasyVideoRetakeKind>
              size="sm"
              aria-label="作り直す部分"
              value={kind}
              onChange={setKind}
              options={[
                { value: 'clip', label: '動きだけ' },
                { value: 'storyboard', label: '絵から' },
              ]}
            />
          )}
          <div className="flex flex-wrap gap-2">
            {RETAKE_CHIPS.map((chip) => (
              <TaskyButton
                key={chip}
                size="sm"
                variant={chips.includes(chip) ? 'default' : 'secondary'}
                aria-pressed={chips.includes(chip)}
                onClick={() => toggleChip(chip)}
              >
                {chip}
              </TaskyButton>
            ))}
          </div>
          <TaskyInput
            aria-label="作り直しの一言"
            value={note}
            maxLength={100}
            placeholder="ひとこと（任意）"
            onChange={(e) => setNote(e.target.value)}
          />
          <div className="flex gap-2">
            <TaskyButton variant="secondary" size="sm" onClick={() => setOpen(false)}>
              やめる
            </TaskyButton>
            <TaskyButton size="sm" className="flex-1" disabled={retakeDisabled} isLoading={submitting} onClick={handleRetake}>
              作り直す（+{unit}cr・残り{left}回）
            </TaskyButton>
          </div>
        </div>
      )}
      {message && <TaskyAlert tone="warning">{message}</TaskyAlert>}
    </TaskyCard>
  );
};

const EasyVideoProgress: React.FC<EasyVideoProgressProps> = ({ project, onUpdate, onBack }) => {
  const [goneMessage, setGoneMessage] = useState<string | null>(null);
  const [recomposing, setRecomposing] = useState(false);
  const [recomposeError, setRecomposeError] = useState<string | null>(null);
  const polling = !isTerminal(project.status) && goneMessage === null;
  useEasyVideoPolling(project.id, polling, onUpdate, setGoneMessage);
  const anyRunning = project.scenes.some((s) => s.storyboard.status === 'running' || s.clip.status === 'running');
  const now = useNow(anyRunning);

  const handleRecompose = async () => {
    setRecomposing(true);
    setRecomposeError(null);
    try {
      onUpdate(await easyVideoService.recompose(project.id));
    } catch (e) {
      setRecomposeError(easyVideoService.extractErrorMessage(e));
    } finally {
      setRecomposing(false);
    }
  };

  if (goneMessage) {
    return (
      <div className="mx-auto w-full max-w-2xl space-y-3 px-4 py-4">
        <TaskyAlert tone="error">{goneMessage}</TaskyAlert>
        <TaskyButton variant="secondary" onClick={onBack}>
          一覧に戻る
        </TaskyButton>
      </div>
    );
  }

  const currentIndex = project.stage ? STAGES.findIndex((s) => s.key === project.stage) : 0;
  const percent = overallPercent(project);
  const etaMinutes =
    project.eta_seconds !== null && project.eta_seconds > 0 ? Math.max(1, Math.ceil(project.eta_seconds / 60)) : null;

  return (
    <div className="mx-auto w-full max-w-4xl space-y-5 px-4 py-4">
      <ol className="flex flex-wrap items-center gap-2" aria-label="作成の進み具合">
        {STAGES.map((stage, i) => {
          const done = project.status === 'completed' || i < currentIndex;
          const current = !done && i === currentIndex;
          const failedHere = current && project.status === 'failed';
          return (
            <li key={stage.key} className="flex items-center gap-2" aria-current={current ? 'step' : undefined}>
              <TaskyBadge variant={failedHere ? 'destructive' : done ? 'success' : current ? 'info' : 'secondary'}>
                {done && <Check className="w-3 h-3" />}
                {stage.label}
              </TaskyBadge>
              {i < STAGES.length - 1 && <span className="text-gray-300">›</span>}
            </li>
          );
        })}
      </ol>

      <TaskyProgress
        value={percent}
        label={project.status === 'failed' ? '途中で止まりました' : etaMinutes ? `あと約${etaMinutes}分` : '作成中'}
        showValue
      />

      {project.status === 'failed' && (
        <TaskyAlert tone="error" title="動画を完成できませんでした">
          {project.error_message || '失敗したシーンを作り直すと、続きから再開します。'}
        </TaskyAlert>
      )}

      {project.final_video?.recomposable === true && (
        <TaskyCard variant="warm" padding="sm" className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-sm text-dark">仕上げ（BGM とカードの合成）で止まりました。シーンはそのまま使えます。</span>
          <TaskyButton size="sm" isLoading={recomposing} onClick={handleRecompose}>
            <RotateCcw />
            仕上げをやり直す（無料）
          </TaskyButton>
          {recomposeError && <TaskyAlert tone="error" className="w-full">{recomposeError}</TaskyAlert>}
        </TaskyCard>
      )}

      {project.status === 'running' && (
        <p className="text-sm text-gray-600">このページを閉じても作成は続きます。</p>
      )}

      {project.scenes.length === 0 ? (
        <TaskyCard variant="warm" padding="md" className="flex items-center gap-3 text-sm text-gray-600">
          <TaskySpinner size="sm" />
          構成を考えています…
        </TaskyCard>
      ) : (
        <div
          className={cn(
            'grid grid-cols-1 gap-4',
            project.aspect_ratio === '16:9' ? 'sm:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-3'
          )}
        >
          {project.scenes.map((scene) => (
            <SceneCard key={scene.id} project={project} scene={scene} now={now} onUpdate={onUpdate} />
          ))}
        </div>
      )}
    </div>
  );
};

export default EasyVideoProgress;
